import React, { useState, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InventoryOptimizer = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [selectedDate, setSelectedDate] = useState("2026-08-10");

  const [inventory, setInventory] = useState([]);
  const [forecastData, setForecastData] = useState([]);
  const [events, setEvents] = useState([]);
  const [signals, setSignals] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    if (selectedStore && selectedDate) {
      setLoading(true);
      Promise.all([fetchInventory(), fetchForecast(), fetchKpis(), fetchEvents(), fetchSignals()])
        .finally(() => setLoading(false));
    }
  }, [selectedStore, selectedDate]);

  const fetchStores = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/forecast/stores`);
      const data = await res.json();
      setStores(data);
      if (data.length > 0) setSelectedStore(data[0].store_id);
    } catch (e) { console.error("Failed to load stores:", e); }
  };

  const fetchInventory = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/forecast/inventory?store=${selectedStore}&date=${selectedDate}`);
      setInventory(await res.json());
    } catch (e) { console.error("Failed to load inventory:", e); }
  };

  const fetchForecast = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/forecast/data?store=${selectedStore}&date=${selectedDate}`);
      const result = await res.json();
      setForecastData(result.data || result);
    } catch (e) { console.error("Failed to load forecast:", e); }
  };

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/forecast/events?date=${selectedDate}`);
      setEvents(await res.json());
    } catch (e) { console.error("Failed to load events:", e); }
  };

  const fetchSignals = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/forecast/signals?store=${selectedStore}&date=${selectedDate}`);
      setSignals(await res.json());
    } catch (e) { console.error("Failed to load signals:", e); }
  };

  const fetchKpis = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/forecast/kpis?store=${selectedStore}&date=${selectedDate}`);
      setKpis(await res.json());
    } catch (e) { console.error("Failed to load KPIs:", e); }
  };

  const getStatus = (item) => {
    if (!item.on_hand_units && item.on_hand_units !== 0) return { label: "No Data", color: "text-slate-400" };
    if (item.on_hand_units <= (item.safety_stock || 0)) return { label: "Order Now", color: "text-red-600 font-semibold" };
    if (item.on_hand_units <= (item.reorder_point || 0)) return { label: "Tight", color: "text-amber-600 font-semibold" };
    return { label: "OK", color: "text-emerald-600 font-semibold" };
  };

  const getForecastForProduct = (productId) => {
    const row = forecastData.find((r) => r.product_id === productId);
    if (!row) return null;
    return {
      units: parseFloat(row.forecast_units).toFixed(1),
      p10: parseFloat(row.forecast_p10).toFixed(1),
      p90: parseFloat(row.forecast_p90).toFixed(1),
    };
  };

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="px-6 py-5 space-y-5">

        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Demand Forecasting & Inventory Optimization</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              SARIMAX-powered demand forecasts with inventory position analysis. Optimizes stock levels to meet SLA while minimizing wastage and holding costs.
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Store</label>
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {stores.map((s) => (
                <option key={s.store_id} value={s.store_id}>
                  {s.store_name} ({s.city})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min="2026-07-01"
              max="2026-12-31"
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          {loading && <p className="text-xs text-emerald-600 animate-pulse pb-2">Loading...</p>}
        </div>

        {/* KPI Cards */}
        {kpis && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Service Level</p>
              <p className={`text-2xl font-bold ${kpis.service_level >= 95 ? "text-emerald-600" : kpis.service_level >= 90 ? "text-amber-600" : "text-red-600"}`}>
                {kpis.service_level}%
              </p>
              <p className="text-xs text-slate-400 mt-1">Target: 95% | Last 30 days</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Stockout Events</p>
              <p className="text-2xl font-bold text-red-600">{kpis.stockout_count}</p>
              <p className="text-xs text-slate-400 mt-1">Lost sales days | 30d window</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Wastage Cost</p>
              <p className="text-2xl font-bold text-amber-600">₹{kpis.wastage_cost.toLocaleString()}</p>
              <p className="text-xs text-slate-400 mt-1">{kpis.units_wasted} units expired | 7d</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Reorder Alerts</p>
              <p className="text-2xl font-bold text-red-600">{kpis.reorder_alerts}</p>
              <p className="text-xs text-slate-400 mt-1">Below reorder point</p>
            </div>
          </div>
        )}

        {/* Event + Signals Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Event Banner */}
          {events.length > 0 && (
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Events Near This Date</p>
              {events.map((e, idx) => (
                <div key={idx} className="flex items-center gap-2 mt-1">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${e.event_type === "festival" ? "bg-purple-500" : e.event_type === "sale_event" ? "bg-blue-500" : "bg-slate-400"}`}></div>
                  <p className="text-sm text-slate-800">
                    <span className="font-medium">{e.event_name}</span>
                    <span className="text-slate-500"> — {e.date?.slice(0, 10)}</span>
                    <span className="text-emerald-600 font-medium"> +{Math.round(((e.expected_lift_pct || 1) - 1) * 100)}%</span>
                    {e.category_affinity && <span className="text-slate-400 text-xs ml-1">({e.category_affinity})</span>}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Local Signals */}
          {signals && (
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Local Signals</p>
              <div className="flex flex-wrap gap-4 text-sm text-slate-700">
                <span>Temp: {signals.temp_c}°C</span>
                <span className={signals.rain_flag ? "text-blue-600 font-medium" : ""}>{signals.rain_flag ? "Rain expected" : "No rain"}</span>
                <span>Footfall: {signals.footfall_index}</span>
                {signals.local_event && <span className="text-purple-700 font-medium">{signals.local_event}</span>}
              </div>
            </div>
          )}
        </div>

        {/* Inventory + Forecast Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">Inventory Position & Demand Forecast</h3>
            <span className="text-xs text-slate-400">{inventory.length} SKUs | {selectedDate}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left border-b border-slate-100">
                  <th className="px-4 py-3 text-xs font-medium text-slate-500">Product</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500">Category</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500">Type</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 text-right">On-Hand</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 text-right">Forecast</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 text-right">P10</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 text-right">P90</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 text-right">Days Cover</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 text-right">Expiry</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {inventory.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-8 text-center text-slate-400">
                      {selectedStore && selectedDate ? "No data for this store and date." : "Select a store and date."}
                    </td>
                  </tr>
                ) : (
                  inventory.map((item, idx) => {
                    const fc = getForecastForProduct(item.product_id);
                    const status = getStatus(item);
                    const daysCover = fc && parseFloat(fc.units) > 0
                      ? ((item.on_hand_units + (item.in_transit_units || 0)) / parseFloat(fc.units)).toFixed(1)
                      : "—";

                    return (
                      <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-4 py-2.5 font-medium text-slate-800">{item.product_name}</td>
                        <td className="px-4 py-2.5 text-slate-600">{item.category}</td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs">
                          {item.product_type === "perishable" ? "Perishable" : item.product_type === "non_perishable" ? "Non-Perishable" : "Long-tail"}
                        </td>
                        <td className={`px-4 py-2.5 text-right font-semibold ${status.label === "Order Now" ? "text-red-700" : "text-slate-800"}`}>
                          {item.on_hand_units}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-slate-800">{fc ? fc.units : "—"}</td>
                        <td className="px-4 py-2.5 text-right text-slate-500">{fc ? fc.p10 : "—"}</td>
                        <td className="px-4 py-2.5 text-right text-slate-500">{fc ? fc.p90 : "—"}</td>
                        <td className={`px-4 py-2.5 text-right font-semibold ${
                          daysCover !== "—" && parseFloat(daysCover) < 2 ? "text-red-600" :
                          daysCover !== "—" && parseFloat(daysCover) < 4 ? "text-amber-600" : "text-emerald-600"
                        }`}>
                          {daysCover !== "—" ? `${daysCover}d` : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {item.days_to_expiry != null ? (
                            <span className={item.days_to_expiry <= 3 ? "text-red-600 font-semibold" : "text-slate-600"}>
                              {item.days_to_expiry}d
                            </span>
                          ) : "—"}
                        </td>
                        <td className={`px-4 py-2.5 text-center text-xs ${status.color}`}>
                          {status.label}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default InventoryOptimizer;
